import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../api/apiClient';
import { extractErrorMessage } from '../../api/extractErrorMessage';

const base = '/trades';

/**
 * Pull the backend's structured error code out of an axios error, regardless
 * of whether the response uses the new envelope (`{ error: { code, message } }`)
 * or a legacy shape. Returns null when no code is present.
 */
const extractErrorCode = (error) => {
  const data = error?.response?.data;
  if (data?.error && typeof data.error === 'object' && typeof data.error.code === 'string') {
    return data.error.code;
  }
  if (typeof data?.code === 'string') {
    return data.code;
  }
  return null;
};

export const listTrades = createAsyncThunk('trades/list', async (params = {}) => {
  const res = await apiClient.get(base, { params });
  return res.data; // { items, cursor }
});

export const loadMoreTrades = createAsyncThunk('trades/loadMore', async (params = {}) => {
  const res = await apiClient.get(base, { params });
  return res.data; // { items, cursor }
});

export const createTrade = createAsyncThunk('trades/create', async (payload) => {
  const res = await apiClient.post(base, payload);
  return res.data.trade;
});

export const getTrade = createAsyncThunk('trades/get', async (tradeId) => {
  const res = await apiClient.get(`${base}/${tradeId}`);
  return res.data; // { trade, recentActions }
});

export const appendTradeAction = createAsyncThunk('trades/appendAction', async ({ tradeId, action }) => {
  const res = await apiClient.post(`${base}/${tradeId}/actions`, action);
  return res.data.action;
});

export const closeTrade = createAsyncThunk('trades/close', async ({ tradeId, exitPrice, exitAt }) => {
  const res = await apiClient.post(`${base}/${tradeId}/close`, { exitPrice, exitAt });
  return res.data.trade;
});

/**
 * Confirm a planned trade. `body` may contain overrides for entryPrice,
 * entryAt, and positionSize; any field omitted defaults server-side to the
 * planned value already on the row.
 */
export const confirmTrade = createAsyncThunk(
  'trades/confirm',
  async ({ tradeId, body = {} }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`${base}/${tradeId}/confirm`, body);
      return res.data?.trade || res.data;
    } catch (error) {
      return rejectWithValue({
        message: extractErrorMessage(error, 'Failed to confirm trade'),
        code: extractErrorCode(error),
        status: error?.response?.status,
      });
    }
  }
);

/**
 * Skip a planned trade. `body.skipReasons` is a multi-select array of canonical
 * enum values (legacy single `body.skipReason` still accepted); `body.skipNote`
 * is required when 'other' is among the reasons.
 */
export const skipTrade = createAsyncThunk(
  'trades/skip',
  async ({ tradeId, body }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post(`${base}/${tradeId}/skip`, body);
      return res.data?.trade || res.data;
    } catch (error) {
      return rejectWithValue({
        message: extractErrorMessage(error, 'Failed to skip trade'),
        code: extractErrorCode(error),
        status: error?.response?.status,
      });
    }
  }
);

export const deleteTrade = createAsyncThunk('trades/delete', async (tradeId) => {
  await apiClient.delete(`${base}/${tradeId}`);
  return tradeId;
});

const tradesSlice = createSlice({
  name: 'trades',
  initialState: {
    items: [],
    cursor: null,
    loading: false,
    loadingMore: false,
    error: null,
    current: null,
    recentActions: [],
  },
  reducers: {
    clearCurrent(state) {
      state.current = null;
      state.recentActions = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listTrades.pending, (state, action) => {
        state.loading = true;
        state.loadingMore = false;
        state.error = null;
        state._listRequestId = action.meta.requestId;
      })
      .addCase(listTrades.fulfilled, (state, action) => {
        // Discard if a newer listTrades request was dispatched while this was in flight
        if (action.meta.requestId !== state._listRequestId) return;
        state.loading = false;
        state.items = action.payload.items;
        state.cursor = action.payload.cursor;
      })
      .addCase(listTrades.rejected, (state, action) => {
        if (action.meta.requestId !== state._listRequestId) return;
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(loadMoreTrades.pending, (state, action) => {
        state.loadingMore = true;
        state._loadMoreListId = state._listRequestId;
      })
      .addCase(loadMoreTrades.fulfilled, (state, action) => {
        state.loadingMore = false;
        // Discard if a new listTrades replaced the data while this was in flight
        if (state._loadMoreListId !== state._listRequestId) return;
        state.items = [...state.items, ...action.payload.items];
        state.cursor = action.payload.cursor;
      })
      .addCase(loadMoreTrades.rejected, (state) => { state.loadingMore = false; })

      .addCase(createTrade.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })

      .addCase(getTrade.fulfilled, (state, action) => {
        state.current = action.payload.trade;
        state.recentActions = action.payload.recentActions || [];
      })

      .addCase(appendTradeAction.fulfilled, (state, action) => {
        state.recentActions.unshift(action.payload);
      })

      .addCase(closeTrade.fulfilled, (state, action) => {
        state.current = action.payload;
        const idx = state.items.findIndex(t => t.tradeId === action.payload.tradeId);
        if (idx >= 0) {
          state.items[idx] = action.payload;
        }
      })
      .addCase(confirmTrade.fulfilled, (state, action) => {
        state.current = action.payload;
        const idx = state.items.findIndex(t => t.tradeId === action.payload.tradeId);
        if (idx >= 0) {
          state.items[idx] = action.payload;
        }
      })
      .addCase(skipTrade.fulfilled, (state, action) => {
        state.current = action.payload;
        const idx = state.items.findIndex(t => t.tradeId === action.payload.tradeId);
        if (idx >= 0) {
          state.items[idx] = action.payload;
        }
      })
      .addCase(deleteTrade.fulfilled, (state, action) => {
        // Remove the trade from the items list
        state.items = state.items.filter(t => t.tradeId !== action.meta.arg);
        // Clear current trade if it was the deleted one
        if (state.current?.tradeId === action.meta.arg) {
          state.current = null;
          state.recentActions = [];
        }
      });
  }
});

export const { clearCurrent } = tradesSlice.actions;
export default tradesSlice.reducer;


